/**
 * LEAD-APPOINTMENT-CRM-COHERENCE-V1 (SCOPE 5 - TRUTH-DISPLAY LAW) - pure
 * appointment-derived milestone + next-action overlay for the canonical Lead 360
 * journey rail.
 *
 * DISPLAY ONLY. This module NEVER writes a stage and NEVER auto-advances one; it
 * reads the appointment section the record already carries and surfaces the
 * strongest downstream FACT (an active appointment) so the rail stops reading
 * "New lead / Make first contact within SLA" while an appointment exists (the
 * founder's E4, leads 87/90). The recorded stage stays authoritative and
 * unchanged; the derived milestone is rendered DISTINCTLY and LABELLED derived.
 *
 * Pure + deterministic (no React, no `Date.now()`): the caller passes `nowMs`, so
 * a test can pin future-vs-past behavior exactly.
 */

import type { Lead360AppointmentSectionView } from '@/dashboard/owner/lead-360-view';

/** Active = the lead currently holds this appointment (mirrors the backend
 *  ACTIVE_SCHEDULING_STATUSES / the workbench ACTIVE_STORED_STATUSES). */
const ACTIVE_STORED_STATUSES: readonly string[] = ['booked', 'confirmed'];

export interface DerivedAppointmentMilestone {
  /** The active appointment's time (UTC ISO). */
  readonly appointmentTime: string;
  /** True when the appointment is now-or-future; false when it is past-dated. */
  readonly isFuture: boolean;
}

/**
 * The lead's single ACTIVE (booked/confirmed) appointment as a derived fact, or
 * null when the record carries no active appointment. Reads ONLY the appointment
 * section the record already holds - it fetches nothing and fabricates nothing.
 */
export function deriveAppointmentMilestone(
  section: Lead360AppointmentSectionView | undefined,
  nowMs: number,
): DerivedAppointmentMilestone | null {
  if (!section || !section.hasAppointments) return null;
  const active = section.cards.find((c) =>
    ACTIVE_STORED_STATUSES.includes(c.storedStatus),
  );
  if (active === undefined) return null;
  const parsed = Date.parse(active.appointmentTime);
  return {
    appointmentTime: active.appointmentTime,
    // A malformed time is treated as future (never a false "date has passed").
    isFuture: Number.isFinite(parsed) ? parsed >= nowMs : true,
  };
}

export const DERIVED_MILESTONE_LABEL = 'Appointment booked';
export const DERIVED_MILESTONE_TAG = 'Derived from appointment';
export const CONFIRM_NEXT_ACTION = 'Confirm the appointment with the patient';
export const REBOOK_NEXT_ACTION =
  'Appointment date has passed - follow up to rebook';

/**
 * The appointment-derived NEXT ACTION, or null when there is no active
 * appointment (the caller then falls back to the stage-contract next action):
 *   - active FUTURE appointment  -> confirm the appointment with the patient
 *   - active PAST-dated appointment -> the date passed; follow up to rebook
 */
export function appointmentDerivedNextAction(
  milestone: DerivedAppointmentMilestone | null,
): string | null {
  if (milestone === null) return null;
  return milestone.isFuture ? CONFIRM_NEXT_ACTION : REBOOK_NEXT_ACTION;
}
